import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtomValue } from "jotai";
import DOMPurify from "dompurify";
import { Header } from "components/common/header/Header";
import { useQuery } from "hooks/useQuery";
import { getLocale, getServiceId } from "common/service";
import { termsStore } from "store/termsStore";
import "./TermsViewer.css";

type Locale = "ko" | "en" | "ja";
export type TermsMode = "terms" | "location";

interface TermsItem {
    termsId: number;
    serviceId?: string;
    locale?: string;
    termsCode: string;
    termsCodeName?: string;
    termsTitle: string;
    termsVersion: string;
    isRequired?: "Y" | "N";
    effectiveDt?: string;
    effectiveDate?: string;
    effectiveStartDate?: string;
    startDate?: string;
    applyStartDate?: string;
    createdAt?: string;
    updatedAt?: string;
}

interface TermsDetail extends Partial<TermsItem> {
    termsContent?: string;
    content?: string;
}

interface TermsViewerProps {
    mode: TermsMode;
}

const normalizeLocale = (value?: string): Locale => {
    const lower = (value || "").toLowerCase();
    if (lower.startsWith("ja")) return "ja";
    if (lower.startsWith("en")) return "en";
    return "ko";
};

const getQueryValue = (value: unknown): string => {
    if (Array.isArray(value)) return String(value[0] || "");
    return String(value || "");
};

const getResponseData = (response: any) => {
    const data = response?.data?.data ?? response?.data ?? response ?? [];
    return data?.list ?? data;
};

const getText = (value?: string) => value || "";

const getEffectiveDate = (term?: Partial<TermsItem | TermsDetail> | null) => {
    return getText(term?.effectiveDt)
        || getText(term?.effectiveDate)
        || getText(term?.effectiveStartDate)
        || getText(term?.startDate)
        || getText(term?.applyStartDate);
};

const formatDate = (value?: string) => {
    if (!value) return "-";

    const date = value.length === 8
        ? new Date(`${value.substring(0, 4)}-${value.substring(4, 6)}-${value.substring(6, 8)}`)
        : new Date(value);

    if (Number.isNaN(date.getTime())) return value;

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");

    return `${yyyy}.${mm}.${dd}`;
};

const compareTerms = (a: TermsItem, b: TermsItem) => {
    const dateA = getEffectiveDate(a) || a.updatedAt || a.createdAt || "";
    const dateB = getEffectiveDate(b) || b.updatedAt || b.createdAt || "";
    if (dateA !== dateB) return dateB.localeCompare(dateA);
    return String(b.termsVersion || "").localeCompare(String(a.termsVersion || ""));
};

const isLocationTerm = (term: TermsItem) => {
    const value = `${term.termsCode} ${term.termsCodeName || ""} ${term.termsTitle || ""}`.toLowerCase();
    return value.includes("location") || value.includes("lbs") || value.includes("\uc704\uce58");
};

const findInitialCode = (termsList: TermsItem[], mode: TermsMode, requestedCode: string) => {
    if (requestedCode) {
        const found = termsList.find((term) => term.termsCode === requestedCode);
        if (found) return found.termsCode;
    }

    const byMode = termsList.find((term) => mode === "location" ? isLocationTerm(term) : !isLocationTerm(term));
    return byMode?.termsCode || termsList[0]?.termsCode || "";
};

export const TermsViewer = ({ mode }: TermsViewerProps) => {
    const { t } = useTranslation();
    const { query } = useQuery();
    const termsListState = useAtomValue(termsStore.termsList.stateAtom);
    const termsDetailState = useAtomValue(termsStore.termsDetail.stateAtom);

    const requestedTermsCode = getQueryValue(query.termsCode);
    const requestedSearchTag = getQueryValue(query.searchTag);

    const locale = normalizeLocale(getLocale());
    const serviceId = getServiceId();
    const searchTag = requestedSearchTag || (mode === "location" ? "lbs" : undefined);

    const [selectedCode, setSelectedCode] = useState("");
    const [selectedTermsId, setSelectedTermsId] = useState<number | null>(null);

    const title = mode === "location"
        ? t("terms.locationTitle")
        : t("terms.title");

    useEffect(() => {
        termsStore.getTermsList(locale, serviceId, searchTag);

        return () => {
            termsStore.termsDetail.onDefault();
        };
    }, [locale, serviceId, searchTag]);

    const termsList: TermsItem[] = useMemo(() => {
        const raw = getResponseData(termsListState.data);
        return Array.isArray(raw) ? raw : [];
    }, [termsListState.data]);

    const groupedTerms = useMemo(() => {
        return termsList.reduce<Record<string, TermsItem[]>>((acc, term) => {
            if (!term.termsCode) return acc;
            acc[term.termsCode] = [...(acc[term.termsCode] || []), term].sort(compareTerms);
            return acc;
        }, {});
    }, [termsList]);

    const codeOptions = useMemo(() => {
        return Object.entries(groupedTerms).map(([code, items]) => ({
            code,
            label: items[0]?.termsCodeName || items[0]?.termsTitle || code,
        }));
    }, [groupedTerms]);

    const selectedVersions = useMemo(() => {
        return selectedCode ? groupedTerms[selectedCode] || [] : [];
    }, [groupedTerms, selectedCode]);

    const selectedTerm = useMemo(() => {
        return selectedVersions.find((term) => term.termsId === selectedTermsId) || selectedVersions[0] || null;
    }, [selectedVersions, selectedTermsId]);

    useEffect(() => {
        if (termsList.length === 0) {
            setSelectedCode("");
            setSelectedTermsId(null);
            return;
        }

        const nextCode = findInitialCode(termsList, mode, requestedTermsCode);
        setSelectedCode((prev) => prev && groupedTerms[prev] ? prev : nextCode);
    }, [termsList, groupedTerms, mode, requestedTermsCode]);

    useEffect(() => {
        if (selectedVersions.length === 0) {
            setSelectedTermsId(null);
            return;
        }

        setSelectedTermsId((prev) => (
            prev && selectedVersions.some((term) => term.termsId === prev)
                ? prev
                : selectedVersions[0].termsId
        ));
    }, [selectedVersions]);

    useEffect(() => {
        if (!selectedTerm?.termsId) return;
        termsStore.getTermsDetail(selectedTerm.termsId);
    }, [selectedTerm?.termsId]);

    const detail: TermsDetail | null = useMemo(() => {
        if (!termsDetailState.isReady) return null;
        const raw = getResponseData(termsDetailState.data);
        return raw || null;
    }, [termsDetailState]);

    const displayTerm = detail || selectedTerm;
    const content = detail?.termsContent || detail?.content || "";
    const isEmpty = termsListState.isReady && termsList.length === 0;

    return (
        <div className="terms-page">
            <Header title={title} isBack />

            <div className="terms-content">
                {termsListState.isPending && (
                    <div className="terms-message">{t("common.loading")}</div>
                )}

                {termsListState.isError && (
                    <div className="terms-message terms-message--error">
                        {t("terms.error")}
                    </div>
                )}

                {isEmpty && !termsListState.isPending && (
                    <div className="terms-message">
                        {t("terms.empty")}
                    </div>
                )}

                {!termsListState.isPending && !termsListState.isError && termsList.length > 0 && (
                    <>
                        <div className="terms-controls">
                            <label className="terms-control">
                                <span>{t("terms.type")}</span>
                                <select
                                    value={selectedCode}
                                    onChange={(event) => {
                                        setSelectedCode(event.target.value);
                                        setSelectedTermsId(null);
                                    }}
                                >
                                    {codeOptions.map((option) => (
                                        <option key={option.code} value={option.code}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className="terms-control">
                                <span>{t("terms.version")}</span>
                                <select
                                    value={selectedTerm?.termsId || ""}
                                    onChange={(event) => setSelectedTermsId(Number(event.target.value))}
                                >
                                    {selectedVersions.map((term) => (
                                        <option key={term.termsId} value={term.termsId}>
                                            v{term.termsVersion} {getEffectiveDate(term) ? `(${formatDate(getEffectiveDate(term))})` : ""}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <section className="terms-document">
                            <h1>{displayTerm?.termsTitle || selectedTerm?.termsTitle || title}</h1>

                            <dl className="terms-meta">
                                <div>
                                    <dt>{t("terms.service")}</dt>
                                    <dd>{serviceId}</dd>
                                </div>
                                <div>
                                    <dt>{t("terms.locale")}</dt>
                                    <dd>{locale}</dd>
                                </div>
                                <div>
                                    <dt>{t("terms.version")}</dt>
                                    <dd>{displayTerm?.termsVersion || selectedTerm?.termsVersion || "-"}</dd>
                                </div>
                                <div>
                                    <dt>{t("terms.effectiveDate")}</dt>
                                    <dd>{formatDate(getEffectiveDate(displayTerm || selectedTerm))}</dd>
                                </div>
                            </dl>

                            {termsDetailState.isPending && (
                                <div className="terms-message">{t("common.loading")}</div>
                            )}

                            {termsDetailState.isError && (
                                <div className="terms-message terms-message--error">
                                    {t("terms.contentError")}
                                </div>
                            )}

                            {!termsDetailState.isPending && !termsDetailState.isError && (
                                content
                                    ? (
                                        <div
                                            className="terms-body"
                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
                                        />
                                    )
                                    : <div className="terms-message">{t("terms.noContent")}</div>
                            )}
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};
